import { IDeviceDetail, deviceSyncSingle, editDevices, getDeviceMqttCredentials, getDeviceStatusRefresh, generateDeviceQrcode, lockDeviceValve } from '@/api/device';
import { AppstoreOutlined, CheckOutlined, CloseOutlined, EditOutlined, EyeInvisibleOutlined, EyeOutlined, SyncOutlined, InfoCircleOutlined, ReloadOutlined, QrcodeOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { Badge, Button, Card, Descriptions, Input, InputNumber, Modal, Typography, message, Image, Space, Tag, Popconfirm, Empty } from 'antd';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import QRCode from 'qrcode';
import '../style.less';
import { ProductInfo } from '../types';
import { useQuery } from '@tanstack/react-query';

const { TextArea } = Input;

const { Text, Link } = Typography;

interface DeviceInfoTabProps {
  detail?: IDeviceDetail;
  productInfo?: ProductInfo;
  navigate: (path: string) => void;
  deviceId: string;
  onRefresh: () => void;
}

function MQTTInfoModal({ deviceId }: { deviceId: string }) {
  const [open, setOpen] = useState<boolean>(false);
  const onCancel = () => {
    setOpen(false);
  };
  const { data, refetch } = useQuery({
    queryKey: ['mqttInfo'],
    queryFn: () => getDeviceMqttCredentials(deviceId),
    enabled: false,
    select: (data) => data?.result,
  });

  return (
    <>
      <Modal title="MQTT连接信息" centered open={open} onCancel={onCancel} footer={null}>
        <div style={{ margin: '20px 0' }}>
          <Descriptions
            column={1}
            bordered
            size="small"
            // layout="vertical"
            styles={{ label: { width: '100px' } }}
            items={[
              {
                key: 'client_id',
                label: '客户端ID',
                children: (
                  <Text style={{ fontSize: 12 }} copyable>
                    {data?.client_id}
                  </Text>
                ),
              },
              {
                key: 'username',
                label: '用户名',
                children: (
                  <Text style={{ fontSize: 12 }} copyable>
                    {data?.username}
                  </Text>
                ),
              },
              {
                key: 'password',
                label: '密码',
                children: (
                  <Text style={{ fontSize: 12 }} copyable>
                    {data?.password}
                  </Text>
                ),
              },
            ]}
          ></Descriptions>
        </div>
      </Modal>
      <div
        onClick={() => {
          setOpen(true);
          refetch();
        }}
        className="flex-center items-center cursor-pointer "
        style={{ padding: '0 4px', cursor: 'pointer', color: '#1677ff' }}
      >
        查看
      </div>
    </>
  );
}

function DeviceStatus({ deviceId }: { deviceId: string }) {
  const { data: status, refetch } = useQuery({
    queryKey: ['deviceStatus', deviceId],
    queryFn: () => getDeviceStatusRefresh(deviceId),
    select: (data) => data?.result?.status,
  });
  return (
    <div className="flex items-center ">
      <Badge
        color={
          {
            在线: 'green',
            离线: 'gray',
            禁用: 'red',
            未激活: 'orange',
          }[status as string] || 'gray'
        }
      />
      <Text className="ml5 mr5" style={{ fontSize: 12 }}>
        {status || '未知'}
      </Text>

      <ReloadOutlined
        onClick={async () => {
          await refetch();
          message.success('刷新成功');
        }}
      ></ReloadOutlined>
    </div>
  );
}

const DeviceInfoTab: React.FC<DeviceInfoTabProps> = ({ detail, productInfo, navigate, deviceId, onRefresh }) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const isCloudDevice = useMemo(() => detail?.cloud_device_id, [detail]);

  const handleEdit = (field: string, currentValue?: string) => {
    setEditingField(field);
    setEditingValues({ [field]: currentValue || '' });
  };

  const handleSave = async (field: string, value?: string) => {
    if (!detail) return;

    // 如果传入了 value，直接使用；否则从 editingValues 中获取
    const finalValue = value !== undefined ? value : editingValues[field] || '';
    setSaving(true);
    try {
      const updateData: any = {
        id: deviceId,
        name: detail.name || '',
        description: detail.description || '',
        image: (detail as any).image || '',
        lon: (detail as any).lon || '',
        lat: (detail as any).lat || '',
        [field]: finalValue,
      };

      // 如果是端口，转换为字符串
      if (field === 'port' && finalValue) {
        updateData[field] = String(finalValue);
      }

      const resp = await editDevices(updateData);
      if (resp.success) {
        message.success('保存成功');
        setEditingField(null);
        setEditingValues({});
        onRefresh();
      } else {
        message.error(resp.errorMsg || '保存失败');
      }
    } catch (error) {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingField(null);
    setEditingValues({});
  };

  const handleValueChange = (field: string, value: string) => {
    setEditingValues((prev) => ({ ...prev, [field]: value }));
  };

  const renderEditableField = (field: string, label: string, value?: string | number, isTextArea?: boolean, plus?: string) => {
    const isEditing = editingField === field;
    const displayValue = `${value || '-'}${plus || ''}`;
    const currentEditingValue = editingValues[field] ?? '';

    if (isEditing) {
      const handleSaveClick = () => {
        // 从 DOM 获取最新值（可能还未触发 onBlur）
        const inputContainer = document.querySelector(`[data-field="${field}"]`);
        let inputValue = '';

        if (['port', 'slaveId', 'period'].includes(field)) {
          // InputNumber 的值
          const inputNumberElement = inputContainer?.querySelector('.ant-input-number-input') as HTMLInputElement;
          if (inputNumberElement) {
            inputValue = inputNumberElement.value;
          }
        } else if (isTextArea) {
          // TextArea 的值
          const textareaElement = inputContainer?.querySelector('textarea') as HTMLTextAreaElement;
          if (textareaElement) {
            inputValue = textareaElement.value;
          }
        } else {
          // Input 的值
          const inputElement = inputContainer?.querySelector('input') as HTMLInputElement;
          if (inputElement) {
            inputValue = inputElement.value;
          }
        }

        // 直接使用从 DOM 获取的值，避免状态更新异步问题
        handleSave(field, inputValue);
      };

      if (isTextArea) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-field={field}>
            <TextArea
              key={`textarea-${field}`}
              defaultValue={currentEditingValue}
              onBlur={(e) => {
                handleValueChange(field, e.target.value);
              }}
              onPressEnter={(e) => {
                const textareaValue = (e.target as HTMLTextAreaElement).value;
                handleValueChange(field, textareaValue);
                handleSaveClick();
              }}
              style={{ fontSize: 12, minHeight: 80 }}
              placeholder={`请输入${label}`}
              autoFocus
              rows={3}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckOutlined style={{ color: '#172c4d', cursor: 'pointer', fontSize: 14 }} onClick={handleSaveClick} disabled={saving} />
              <CloseOutlined style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: 14 }} onClick={handleCancel} />
            </div>
          </div>
        );
      }

      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} data-field={field}>
          {['port', 'slaveId', 'period'].includes(field) ? (
            <InputNumber
              key={`input-${field}`}
              defaultValue={currentEditingValue ? Number(currentEditingValue) : undefined}
              onBlur={(e) => {
                console.log(e);
                // // InputNumber 的 onBlur 事件，需要通过 DOM 获取实际值
                // const inputContainer = document.querySelector(`[data-field="${field}"]`);
                // const inputNumberElement = inputContainer?.querySelector('.ant-input-number-input') as HTMLInputElement;
                // if (inputNumberElement) {
                //   handleValueChange(field, inputNumberElement.value);
                // }
                handleValueChange(field, e.target.value);
              }}
              onPressEnter={(e) => {
                handleValueChange(field, (e.target as HTMLInputElement).value);
                handleSaveClick();
              }}
              style={{ width: 120, fontSize: 12 }}
              size="small"
              placeholder={`请输入${label}`}
            />
          ) : (
            <Input
              key={`input-${field}`}
              defaultValue={currentEditingValue}
              onBlur={(e) => {
                handleValueChange(field, e.target.value);
              }}
              onPressEnter={(e) => {
                handleValueChange(field, (e.target as HTMLInputElement).value);
                handleSaveClick();
              }}
              style={{ width: 150, fontSize: 12 }}
              size="small"
              placeholder={`请输入${label}`}
            />
          )}
          <CheckOutlined
            style={{ color: '#172c4d', cursor: 'pointer', fontSize: 12 }}
            onClick={handleSaveClick}
            // disabled={saving}
          />
          <CloseOutlined style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: 12 }} onClick={handleCancel} />
        </span>
      );
    }

    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <span>{displayValue}</span>
        <EditOutlined style={{ color: '#172c4d', cursor: 'pointer', fontSize: 12 }} onClick={() => handleEdit(field, value ? String(value) : '')} />
      </span>
    );
  };

  // 设备信息块
  const DeviceInfoBlock = () => (
    <Card
      title={
        <span>
          <AppstoreOutlined style={{ color: '#172c4d', marginRight: 8 }} />
          设备信息
        </span>
      }
      style={{ padding: '20px 0', border: 'none', borderBottom: '1px solid #f0f0f0' }}
    >
      <Descriptions
        column={4}
        size="small"
        // bordered
        styles={{ label: { fontSize: 12, color: '#8a949c' }, content: { fontSize: 12 } }}
      >
        <Descriptions.Item label="设备ID" className="device-info-narrow-col">
          <Text style={{ fontSize: 12 }} copyable>
            {detail?.id || '-'}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label="设备名称" className="device-info-narrow-col">
          {renderEditableField('name', '设备名称', detail?.name)}
        </Descriptions.Item>
        <Descriptions.Item label="设备密钥">
          {detail?.secret ? (
            <>
              {visible ? (
                <>
                  <Text style={{ fontSize: 12 }} className="mr10" copyable>
                    {detail.secret}
                  </Text>
                  <EyeInvisibleOutlined onClick={() => setVisible(false)} style={{ cursor: 'pointer' }} />
                </>
              ) : (
                <>
                  <span className="mr10">{new Array(detail.secret.length).fill(null).map(() => '*')}</span>
                  <EyeOutlined onClick={() => setVisible(true)} style={{ cursor: 'pointer' }} />
                </>
              )}
            </>
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="创建时间">{detail?.create_at ? dayjs(detail.create_at).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
        <Descriptions.Item label="设备类型">
          {detail?.device_type || '-'}
          {detail?.child_device_count ? ` (${detail.child_device_count}个子设备)` : ''}
        </Descriptions.Item>
        <Descriptions.Item label="是否为视频设备" className="device-info-narrow-col">
          {detail?.is_ipc ? '是' : '否'}
        </Descriptions.Item>
        <Descriptions.Item label="设备状态" className="device-info-narrow-col">
          <DeviceStatus deviceId={deviceId}></DeviceStatus>
        </Descriptions.Item>
        <Descriptions.Item label="最后数据上报时间" className="device-info-wide-col">
          {detail?.last_update_ts ? dayjs(detail.last_update_ts).format('YYYY-MM-DD HH:mm:ss') : detail?.last_online_time ? dayjs(detail.last_online_time).format('YYYY-MM-DD HH:mm:ss') : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="设备位置" span={1}>{detail?.location || '-'}</Descriptions.Item>
        <Descriptions.Item label="设备描述" span={3}>
          {renderEditableField('description', '设备描述', detail?.description)}
        </Descriptions.Item>
        <Descriptions.Item label="设备分组" span={4}>
          {detail?.device_group_name || '-'}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );

  // 扩展信息块
  const ExtendedInfoBlock = () => (
    <Card
      title={
        <span>
          <AppstoreOutlined style={{ color: '#172c4d', marginRight: 8 }} />
          扩展信息
        </span>
      }
      style={{ padding: '20px 0', border: 'none', borderBottom: '1px solid #f0f0f0' }}
    >
      <Descriptions
        column={4}
        size="small"
        // bordered
        styles={{ label: { fontSize: 12, color: '#8a949c' }, content: { fontSize: 12 } }}
      >
        <Descriptions.Item label="MQTT连接信息">
          <MQTTInfoModal deviceId={deviceId} />
        </Descriptions.Item>
        <Descriptions.Item label="设备唯一标识">{renderEditableField('device_sn', '设备唯一标识', detail?.device_sn)}</Descriptions.Item>
        <Descriptions.Item label="IMEI">{renderEditableField('imei', 'IMEI', detail?.imei)}</Descriptions.Item>
        <Descriptions.Item label="IP地址">{renderEditableField('ip', 'IP地址', detail?.ip)}</Descriptions.Item>
        <Descriptions.Item label="端口">{renderEditableField('port', '端口', detail?.port)}</Descriptions.Item>
        <Descriptions.Item label="工厂">{renderEditableField('manufacturer', '工厂', detail?.manufacturer)}</Descriptions.Item>
        <Descriptions.Item label="型号">{renderEditableField('model', '型号', detail?.model)}</Descriptions.Item>
        <Descriptions.Item label="固件版本">{renderEditableField('firmware', '固件版本', detail?.firmware)}</Descriptions.Item>
        <Descriptions.Item label="设备ID">{renderEditableField('slaveId', '设备ID', detail?.slaveId)}</Descriptions.Item>
        <Descriptions.Item label="扫描周期" span={3}>{renderEditableField('period', '扫描周期', detail?.period, false, '秒')}</Descriptions.Item>
      </Descriptions>
    </Card>
  );

  // 产品信息块
  const ProductInfoBlock = () => (
    <Card
      title={
        <span>
          <AppstoreOutlined style={{ color: '#172c4d', marginRight: 8 }} />
          产品信息
        </span>
      }
      style={{ padding: '20px 0', border: 'none', borderBottom: '1px solid #f0f0f0' }}
    >
      <Descriptions
        column={4}
        size="small"
        // bordered
        styles={{ label: { fontSize: 12, color: '#8a949c' }, content: { fontSize: 12 } }}
      >
        <Descriptions.Item label="产品ID">
          <Text style={{ fontSize: 12 }} copyable>
            {productInfo?.id || '-'}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label="产品名称">
          {detail?.product_name || productInfo?.name ? (
            <Link
              style={{ fontSize: 12 }}
              onClick={() => {
                navigate(`/gateway/product/detail/${detail?.product_id}`);
              }}
            >
              {detail?.product_name || productInfo?.name}
            </Link>
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="产品编码">{detail?.product_key || '-'}</Descriptions.Item>
        <Descriptions.Item label="产品品牌">{detail?.product_brand || '-'}</Descriptions.Item>
        <Descriptions.Item label="节点类型">{detail?.product_node_type || productInfo?.node_type || '-'}</Descriptions.Item>
        <Descriptions.Item label="接入协议" span={3}>{detail?.product_protocol || productInfo?.protocol || '-'}</Descriptions.Item>
        <Descriptions.Item label="产品描述" span={4}>
          {detail?.product_description || productInfo?.description || '-'}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );

  // 通讯信息块
  const CommunicationInfoBlock = () => {
    const isModbus = productInfo?.protocol === 'Modbus';
    return (
      <Card
        title={
          <span>
            <AppstoreOutlined style={{ color: '#172c4d', marginRight: 8 }} />
            通讯信息
          </span>
        }
        style={{ padding: '20px 0', border: 'none', borderBottom: '1px solid #f0f0f0' }}
      >
        <Descriptions
          column={4}
          size="small"
          // bordered
          styles={{ label: { fontSize: 12, color: '#8a949c', width: '15%', minWidth: 100 }, content: { fontSize: 12, width: '35%' } }}
        >
          {isModbus ? (
            <>
              <Descriptions.Item label="Modbus Slave ID" span={2}>{detail?.slaveId || '-'}</Descriptions.Item>
              <Descriptions.Item label="采集周期" span={2}>{detail?.period || '-'}</Descriptions.Item>
            </>
          ) : (
            <>
              <Descriptions.Item label="客户端ID">
                {detail?.id ? (
                  <Text style={{ fontSize: 12 }} copyable>
                    {detail.id}
                  </Text>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="用户名">
                {detail?.id ? (
                  <Text style={{ fontSize: 12 }} copyable>
                    {detail.id}
                  </Text>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="密码" span={2}>
                {detail?.secret ? (
                  <>
                    {passwordVisible ? (
                      <>
                        <Text style={{ fontSize: 12 }} className="mr10" copyable>
                          {detail.secret}
                        </Text>
                        <EyeInvisibleOutlined onClick={() => setPasswordVisible(false)} style={{ cursor: 'pointer' }} />
                      </>
                    ) : (
                      <>
                        <span className="mr10">{new Array(detail.secret.length).fill(null).map(() => '*')}</span>
                        <EyeOutlined onClick={() => setPasswordVisible(true)} style={{ cursor: 'pointer' }} />
                      </>
                    )}
                  </>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
      </Card>
    );
  };

  // 激活管理块
  const ActivationManagementBlock = () => {
    const [qrcodeDataUrl, setQrcodeDataUrl] = useState('');
    const [qrcodeVisible, setQrcodeVisible] = useState(false);
    const [loadingQrcode, setLoadingQrcode] = useState(false);

    /** 加载二维码 */
    const handleLoadQrcode = async () => {
      setLoadingQrcode(true);
      setQrcodeVisible(true);
      try {
        const resp = await generateDeviceQrcode(deviceId);
        if (resp.success && resp.result) {
          const code = resp.result.activation_code || resp.result.device_sn;
          // 前端生成二维码图片
          const dataUrl = await QRCode.toDataURL(code, {
            width: 260,
            margin: 2,
            color: { dark: '#000000', light: '#ffffff' },
          });
          setQrcodeDataUrl(dataUrl);
        }
      } catch (err) {
        console.error('生成二维码失败:', err);
      } finally {
        setLoadingQrcode(false);
      }
    };

    return (
      <Card
        title={
          <span>
            <QrcodeOutlined style={{ color: '#172c4d', marginRight: 8 }} />
            激活与二维码管理
          </span>
        }
        style={{ padding: '20px 0', border: 'none', borderBottom: '1px solid #f0f0f0' }}
        extra={
          <Space>
            <Button size="small" icon={<QrcodeOutlined />} onClick={handleLoadQrcode} loading={loadingQrcode}>
              查看二维码
            </Button>
            {detail?.is_activated === false ? (
              <Tag icon={<LockOutlined />} color="orange">未激活</Tag>
            ) : (
              <Tag color="green">已激活</Tag>
            )}
          </Space>
        }
      >
        <Descriptions column={4} size="small" styles={{ label: { fontSize: 12, color: '#8a949c' }, content: { fontSize: 12 } }}>
          <Descriptions.Item label="设备编码">{detail?.device_sn || '-'}</Descriptions.Item>
          <Descriptions.Item label="激活状态">
            {detail?.is_activated === false ? (
              <Tag icon={<LockOutlined />} color="orange">未激活（扫码后使用）</Tag>
            ) : (
              <Tag color="green">已激活</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="电磁阀状态">
            {detail?.valve_locked ? (
              <Tag icon={<LockOutlined />} color="red">已锁止</Tag>
            ) : (
              <Tag color="green">正常</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="说明">
            <Text type="secondary" style={{ fontSize: 11 }}>二维码粘贴到设备上，服务商扫码激活</Text>
          </Descriptions.Item>
        </Descriptions>

        {/* 二维码弹窗 */}
        <Modal title="设备激活二维码" open={qrcodeVisible} onCancel={() => setQrcodeVisible(false)} footer={null} width={420} centered>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            {loadingQrcode ? <div style={{ padding: 40 }}>生成中...</div> : qrcodeDataUrl ? (
              <>
                <Image src={qrcodeDataUrl} alt="设备二维码" width={260} height={260} preview={false} />
                <div style={{ marginTop: 16, fontSize: 14 }}>
                  <p><strong>设备名称：</strong>{detail?.name}</p>
                  <p><strong>设备编码：</strong>{detail?.device_sn || '-'}</p>
                  <p style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
                    将此二维码打印并粘贴到设备外壳上，服务商使用小程序扫码即可激活设备
                  </p>
                </div>
                <Space style={{ marginTop: 16 }}>
                  <Button size="small" onClick={() => {
                    if (qrcodeDataUrl) { const link = document.createElement('a'); link.href = qrcodeDataUrl; link.download = `qrcode_${detail?.device_sn || deviceId}.png`; link.click(); }
                  }}>下载</Button>
                </Space>
              </>
            ) : <Empty description="暂无数据" />}
          </div>
        </Modal>
      </Card>
    );
  };

  return (
    <div className="device-info-tab">
      {isCloudDevice && (
        <div style={{ padding: '20px 0', textAlign: 'right' }}>
          <Button
            type="link"
            size="small"
            loading={submitting}
            disabled={submitting}
            icon={<SyncOutlined />}
            style={{ fontSize: 12 }}
            onClick={() => {
              setSubmitting(true);
              deviceSyncSingle({ device_id: deviceId })
                .then((resp) => {
                  if (resp.success) {
                    message.success('同步成功');
                    onRefresh();
                  } else {
                    message.error(resp.errorMsg);
                  }
                })
                .finally(() => {
                  setSubmitting(false);
                });
            }}
          >
            同步设备
          </Button>
        </div>
      )}
      <DeviceInfoBlock />
      <ActivationManagementBlock />
      <ExtendedInfoBlock />
      <ProductInfoBlock />
      {/* <CommunicationInfoBlock /> */}
    </div>
  );
};

export default DeviceInfoTab;
